import Image from '@tiptap/extension-image';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import ImageNodeView from './ImageNodeView.vue';

/**
 * Custom Image extension — extends the base Tiptap Image with:
 * - data-align attribute for positioning (left/center/right)
 * - width attribute persistence for resize
 * - Vue NodeView component with drag-resize handles and floating toolbar
 *
 * imageUploadHandler option is threaded through to the NodeView
 * so it can re-upload images when src is changed via URL input.
 */
const CustomImage = Image.extend({
  name: 'image',

  addOptions() {
    return {
      ...this.parent?.(),
      // Callback for uploading images to S3, passed from Tiptap-editor
      imageUploadHandler: null,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),

      // Alignment: left, center, right — positions image without text wrapping
      'data-align': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-align') || null,
        renderHTML: (attributes) => {
          if (!attributes['data-align']) return {};
          return { 'data-align': attributes['data-align'] };
        },
      },

      // Float: when enabled, text wraps around the image (used with left/right align)
      'data-float': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-float') || null,
        renderHTML: (attributes) => {
          if (!attributes['data-float']) return {};
          return { 'data-float': attributes['data-float'] };
        },
      },

      width: {
        default: null,
        parseHTML: (element) => {
          const attrWidth = element.getAttribute('width');
          const styleWidth = element.style.width;
          const raw = attrWidth || styleWidth || null;
          if (!raw) return null;
          // Normalize bare numbers to px
          if (/^\d+$/.test(raw)) return `${raw}px`;
          return raw;
        },
        renderHTML: (attributes) => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },

      height: {
        default: 'auto',
        parseHTML: () => 'auto',
        renderHTML: () => ({ height: 'auto' }),
      },
    };
  },

  addNodeView() {
    return VueNodeViewRenderer(ImageNodeView);
  },
});

export default CustomImage;
